import React from "react";
import PropTypes from "prop-types";
import { cn } from "../util";

function getNestedError(errors, name) {
  return name.split(".").reduce((acc, part) => acc?.[part], errors);
}

export function FormInput({
  className,
  name,
  label,
  placeholder = "",
  type = "text",
  variant = "input",
  options = [],
  errors,
  register,
  rules = {},
  isLoading = false,
  ...rest
}) {
  const error = getNestedError(errors, name);
  const hasError = !!error;

  return (
    <div className={className}>
      <label htmlFor={name} className="block text-sm font-medium text-gray-500">
        {label}
      </label>
      {variant === "input" && (
        <div className="relative">
          <input
            id={name}
            type={type}
            placeholder={placeholder}
            className={cn(
              "font-normal border-v2-gray-border-tables text-v2-text-bar-steps focus:ring-v2-blue-text-login focus:border-v2-blue-text-login transition-all block w-full px-4 my-1 rounded-md disabled:cursor-not-allowed read-only:text-gray-500",
              hasError && "border-red-500",
              name.includes("max_discount") && "pl-6",
            )}
            autoComplete="off"
            {...(type === "number" ? { step: "0.01" } : {})}
            {...register(name, rules)}
            {...rest}
          />
          {name.includes("max_discount") && (
            <span className="absolute top-1.5 font-normal text-v2-text-bar-steps text-lg left-2">
              $
            </span>
          )}
        </div>
      )}
      {variant === "textarea" && (
        <textarea
          id={name}
          className={cn(
            "font-normal border-v2-gray-border-tables text-v2-text-bar-steps focus:ring-v2-blue-text-login focus:border-v2-blue-text-login transition-all block w-full px-4 my-1 rounded-md disabled:cursor-not-allowed",
            hasError && "border-red-500",
            "resize-none",
          )}
          placeholder={placeholder}
          rows={2}
          {...register(name, rules)}
          {...rest}
        />
      )}
      {variant === "select" && (
        <select
          id={name}
          className={cn(
            "font-normal border-v2-gray-border-tables text-v2-text-bar-steps focus:ring-v2-blue-text-login focus:border-v2-blue-text-login transition-all block w-full px-4 my-1 rounded-md",
            "disabled:cursor-not-allowed",
            hasError && "border-red-500",
          )}
          {...register(name, rules)}
          {...rest}
          disabled={isLoading}
        >
          <option value="">
            {isLoading ? "Cargando..." : "Selecciona una opción"}
          </option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      )}
      <p
        role="alert"
        className={cn(
          "mt-1 text-xs text-red-500",
          hasError ? "visible" : "invisible",
        )}
      >
        {error?.message || "Error"}
      </p>
    </div>
  );
}

FormInput.propTypes = {
  className: PropTypes.string,
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  type: PropTypes.string,
  variant: PropTypes.oneOf(["input", "textarea", "select"]),
  options: PropTypes.array,
  errors: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  rules: PropTypes.object.isRequired,
  rest: PropTypes.object,
  isLoading: PropTypes.bool,
};
